import React, { useState, useEffect } from 'react';
import { X, CreditCard, Loader2, CheckCircle, AlertCircle, Shield } from 'lucide-react';
import { useStax } from '@/hooks/useStax';
import { updatePaymentMethod } from '@/lib/portal-api-client';

interface UpdatePaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  membershipId: string;
  onUpdateComplete: () => void;
}

export function UpdatePaymentModal({
  isOpen,
  onClose,
  membershipId,
  onUpdateComplete,
}: UpdatePaymentModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { isStaxReady, staxError, cardFormStatus, retryCardForm, tokenizePaymentMethod } = useStax();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSuccess(false);
      setError('');
    }
  }, [isOpen]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const form = e.currentTarget as HTMLFormElement;

      const cardholderName = (
        form.elements.namedItem('cardholder-name') as HTMLInputElement | null
      )?.value?.trim();

      const billingZip = (
        form.elements.namedItem('billing-zip') as HTMLInputElement | null
      )?.value?.trim();

      const nameParts = (cardholderName || '').split(/\s+/).filter(Boolean);
      const firstname = nameParts[0] || '';
      const lastname = nameParts.slice(1).join(' ');

      if (!firstname || !lastname) {
        throw new Error('Please enter the full cardholder name (first and last).');
      }

      // Tokenize the payment method using Stax
      const paymentToken = await tokenizePaymentMethod({
        form,
        extraDetails: {
          firstname,
          lastname,
          person_name: cardholderName || `${firstname} ${lastname}`.trim(),
          address_zip: billingZip || undefined,
          address_country: 'USA',
        },
      });

      if (!paymentToken) {
        throw new Error('Failed to tokenize payment method. Please check your card details.');
      }

      // Update payment method via API
      await updatePaymentMethod(membershipId, paymentToken, 'card');

      setSuccess(true);
      setTimeout(() => {
        onUpdateComplete();
        onClose();
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Failed to update payment method. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
        {/* Header */}
        <div className="border-b border-gray-200 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Update Payment Method</h2>
              <p className="text-sm text-gray-600">Change your card on file</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Success State */}
        {success && (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Payment Method Updated!</h3>
            <p className="text-gray-600">
              Your new card has been saved and will be used for future payments.
            </p>
          </div>
        )}

        {/* Form */}
        {!success && (
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Security Notice */}
            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold">Secure Payment Processing</p>
                <p className="mt-1">
                  Your payment information is encrypted and processed securely. We never store your
                  full card details.
                </p>
              </div>
            </div>

            {/* Card Form */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Card Information
              </label>

              {cardFormStatus !== 'mounted' && (
                <div
                  className={`mb-4 rounded-lg border px-3 py-2 text-xs flex items-start gap-2 ${
                    cardFormStatus === 'loading'
                      ? 'bg-blue-50 border-blue-200 text-blue-800'
                      : 'bg-red-50 border-red-200 text-red-700'
                  }`}
                >
                  {cardFormStatus === 'loading' ? (
                    <>
                      <Loader2 className="w-4 h-4 mt-0.5 animate-spin" />
                      <span>Loading secure card fields…</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-4 h-4 mt-0.5" />
                      <div className="flex-1">
                        <div className="font-semibold">Card fields failed to load</div>
                        <div className="mt-0.5">
                          {staxError ||
                            (isStaxReady
                              ? 'Please refresh and try again.'
                              : 'Payment system is still loading. Please try again in a moment.')}
                        </div>
                      </div>
                      <button type="button" onClick={retryCardForm} className="shrink-0 font-semibold underline">
                        Retry
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* Card Number */}
              <div className="mb-4">
                <label htmlFor="card-number" className="block text-sm text-gray-600 mb-2">
                  Card Number
                </label>
                <div
                  id="card-number"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent"
                  style={{ minHeight: '48px' }}
                />
              </div>

              {/* Expiration (month/year) and CVV */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-2">
                    Expiration Date
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      id="card-exp-month"
                      name="month"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="" disabled>MM</option>
                      {Array.from({ length: 12 }, (_, i) => {
                        const m = (i + 1).toString().padStart(2, '0');
                        return <option key={m} value={m}>{m}</option>;
                      })}
                    </select>
                    <select
                      id="card-exp-year"
                      name="year"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="" disabled>YYYY</option>
                      {Array.from({ length: 12 }, (_, i) => {
                        const y = new Date().getFullYear() + i;
                        return <option key={y} value={y}>{y}</option>;
                      })}
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="card-cvv" className="block text-sm text-gray-600 mb-2">
                    CVV
                  </label>
                  <div
                    id="card-cvv"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent"
                    style={{ minHeight: '48px' }}
                  />
                </div>
              </div>
            </div>

            {/* Cardholder Name */}
            <div>
              <label htmlFor="cardholder-name" className="block text-sm font-semibold text-gray-700 mb-2">
                Cardholder Name
              </label>
              <input
                id="cardholder-name"
                name="cardholder-name"
                type="text"
                placeholder="Name on card"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* ZIP Code */}
            <div>
              <label htmlFor="zip-code" className="block text-sm font-semibold text-gray-700 mb-2">
                Billing ZIP Code
              </label>
              <input
                id="zip-code"
                name="billing-zip"
                type="text"
                placeholder="12345"
                maxLength={10}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 py-3 px-6 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:border-gray-400 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !isStaxReady || cardFormStatus !== 'mounted'}
                className="flex-1 py-3 px-6 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-semibold rounded-lg hover:from-green-700 hover:to-emerald-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-5 h-5" />
                    Update Card
                  </>
                )}
              </button>
            </div>

            {/* PCI Compliance Notice */}
            <div className="text-center pt-2">
              <p className="text-xs text-gray-500">
                🔒 PCI DSS compliant payment processing by Stax
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}